import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import api from '@src/api/clients';
import {
  approveBudgetCenter,
  discardBudgetCenterDraft,
  fetchBudgetCenter,
  saveBudgetCenterDraft,
} from '@src/api/actions/dashboard/clientBudgets';
import Page from '@src/components/layout/Page';
import { useAuth } from '@src/context';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

function formatCurrency(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '$0.00';
  }
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatNumberInput(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const raw = String(value).replace(/,/g, '');
  if (!/^[-]?\d*\.?\d*$/.test(raw)) {
    return String(value);
  }
  const [whole, fraction] = raw.split('.');
  const normalizedWhole = whole === '' || whole === '-' ? whole : Number(whole).toLocaleString('en-US');
  if (fraction !== undefined) {
    return `${normalizedWhole}.${fraction}`;
  }
  return normalizedWhole;
}

function getErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (!data) {
    return fallback;
  }
  if (typeof data === 'string') {
    return data;
  }
  if (data.detail) {
    return data.detail;
  }
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
    .join(', ');
}

function getCurrentMonthStart() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10);
}

function formatMonthLabel(value) {
  if (!value) {
    return 'Current Month';
  }
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

function formatDateTime(value) {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatHistoryValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'Empty';
  }
  if (typeof value === 'object') {
    if (value.name) {
      return value.name;
    }
    if (Array.isArray(value)) {
      return value.join(', ') || 'Empty';
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function formatStatusLabel(value) {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'active' || normalized === 'approved') {
    return 'Live';
  }
  if (normalized === 'pending_approval') {
    return 'Pending Review';
  }
  if (normalized === 'draft') {
    return 'Draft';
  }
  if (normalized === 'archived') {
    return 'Archived';
  }
  return normalized ? normalized.replaceAll('_', ' ') : 'Draft';
}

function getStatusChipColor(value) {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'active' || normalized === 'approved') {
    return 'success';
  }
  if (normalized === 'pending_approval') {
    return 'warning';
  }
  if (normalized === 'archived') {
    return 'default';
  }
  return 'info';
}

function SummaryCard({ label, value, helper }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent>
        <Stack spacing={1}>
          <Typography color="text.secondary" variant="overline">
            {label}
          </Typography>
          <Typography variant="h5">{value}</Typography>
          <Typography color="text.secondary" variant="body2">
            {helper}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function emptySegment(period) {
  return {
    budget_id: null,
    source_budget_id: null,
    vertical: '',
    location: '',
    channel: '',
    amount: '',
    notes: '',
    adjustment_total: '0.00',
    effective_amount: '',
    workflow_status: 'draft',
    adjustments: [],
    vertical_data: null,
    location_data: null,
    channel_data: null,
    pendingAdjustment: {
      amount_delta: '',
      effective_date: period,
      notes: '',
    },
  };
}

function normalizeSegment(segment, period) {
  return {
    budget_id: segment?.budget_id ?? segment?.id ?? null,
    source_budget_id: segment?.source_budget_id ?? segment?.id ?? null,
    vertical: segment?.vertical ?? segment?.vertical_data?.id ?? '',
    location: segment?.location ?? segment?.location_data?.id ?? '',
    channel: segment?.channel ?? segment?.channel_data?.id ?? '',
    amount: segment?.amount ?? '',
    notes: segment?.notes ?? '',
    adjustment_total: segment?.adjustment_total ?? '0.00',
    effective_amount: segment?.effective_amount ?? segment?.amount ?? '',
    workflow_status: segment?.workflow_status ?? 'draft',
    adjustments: Array.isArray(segment?.adjustments) ? segment.adjustments : [],
    vertical_data: segment?.vertical_data ?? null,
    location_data: segment?.location_data ?? null,
    channel_data: segment?.channel_data ?? null,
    pendingAdjustment: {
      amount_delta: '',
      effective_date: period,
      notes: '',
    },
  };
}

function segmentSignature(segment) {
  return [
    segment.vertical || '',
    segment.location || '',
    segment.channel || '',
    String(segment.amount || ''),
    String(segment.notes || ''),
  ].join('|');
}

function buildSegmentLabel(segment, options) {
  const verticalLabel = options.verticals.find((row) => String(row.value) === String(segment.vertical || ''))?.label;
  const locationLabel = options.locations.find((row) => String(row.value) === String(segment.location || ''))?.label;
  return [verticalLabel, locationLabel].filter(Boolean).join(' / ') || 'Segment';
}

function isExistingSegment(segment) {
  return Boolean(segment?.budget_id);
}

function isLiveOriginSegment(segment, liveBudgetIds) {
  const sourceBudgetId = segment?.source_budget_id;
  if (!sourceBudgetId) {
    return false;
  }
  return Array.isArray(liveBudgetIds) && liveBudgetIds.includes(sourceBudgetId);
}

export default function BudgetCenterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useAuth();
  const user = state?.user;
  const memberships = user?.client_memberships || [];
  const [selectedClient, setSelectedClient] = useState(searchParams.get('client_id') || '');
  const [selectedClientOption, setSelectedClientOption] = useState(null);
  const [period, setPeriod] = useState(searchParams.get('period') || getCurrentMonthStart());
  const [pageData, setPageData] = useState(null);
  const [segments, setSegments] = useState([emptySegment(period)]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [adjustmentsDialogOpen, setAdjustmentsDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [turnOffDialogSegment, setTurnOffDialogSegment] = useState(null);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(null);
  const [verticalOptions, setVerticalOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  const {
    options: clientOptions,
    loading: clientLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue,
  } = useClientAutocomplete();

  const isStaff = Boolean(user?.is_staff || user?.is_superuser);
  const canShowClientSelector = isStaff || clientOptions.length > 1;

  useEffect(() => {
    if (selectedClient) {
      const option = findOptionByValue(selectedClient);
      if (option) {
        setSelectedClientOption(option);
      }
      api.request({
        url: `dashboard/client-budgets/budget-verticals/?client_id=${selectedClient}`,
        method: 'GET',
      })
        .then((response) => {
          const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
          setVerticalOptions(rows.map((row) => ({ value: row.id, label: row.vertical })));
        })
        .catch(() => setVerticalOptions([]));
      api.request({
        url: 'dashboard/client-territories/',
        method: 'GET',
        payload: { client_id: selectedClient },
      })
        .then((response) => {
          const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
          setLocationOptions(rows.map((row) => ({ value: row.id, label: row.name })));
        })
        .catch(() => setLocationOptions([]));
      return;
    }

    setVerticalOptions([]);
    setLocationOptions([]);

    const fallbackClientId = searchParams.get('client_id')
      || memberships.find((membership) => membership.client_id)?.client_id
      || (clientOptions.length === 1 ? clientOptions[0].value : '');
    if (fallbackClientId) {
      setSelectedClient(String(fallbackClientId));
    }
  }, [clientOptions, findOptionByValue, memberships, searchParams, selectedClient]);

  const loadBudgetCenter = useCallback(() => {
    if (!selectedClient) {
      return;
    }
    setLoading(true);
    fetchBudgetCenter({ client_id: selectedClient, period })
      .then((response) => {
        const payload = response.data;
        setPageData(payload);
        setSegments(
          Array.isArray(payload?.segments) && payload.segments.length
            ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
            : [emptySegment(payload?.period || period)]
        );
      })
      .catch((error) => {
        setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to load Budget Center.') });
      })
      .finally(() => setLoading(false));
  }, [period, selectedClient]);

  useEffect(() => {
    loadBudgetCenter();
  }, [loadBudgetCenter]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (selectedClient) {
      nextParams.set('client_id', String(selectedClient));
    } else {
      nextParams.delete('client_id');
    }
    if (period) {
      nextParams.set('period', period);
    }
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [period, searchParams, selectedClient, setSearchParams]);

  const currentSummary = pageData?.current_budget || null;
  const previousSummary = pageData?.previous_budget || null;
  const isCurrentMonth = dayjs(period).isSame(dayjs(), 'month');
  const isFutureMonth = dayjs(period).isAfter(dayjs(), 'month');
  const hasLiveBudgetRows = Array.isArray(pageData?.live_budget_ids) && pageData.live_budget_ids.length > 0;
  const isLiveMonth = isCurrentMonth && (
    hasLiveBudgetRows || ['active', 'approved'].includes(String(pageData?.status || '').toLowerCase())
  );
  const isPlanningMode = isFutureMonth || pageData?.seeded_from_previous;
  const showAdjustmentColumns = isLiveMonth;
  const optionSet = useMemo(
    () => ({ verticals: verticalOptions, locations: locationOptions }),
    [verticalOptions, locationOptions]
  );
  const selectedSegment = selectedSegmentIndex === null ? null : segments[selectedSegmentIndex] || null;

  const buildPayload = useCallback(() => ({
    client_id: Number(selectedClient),
    period,
    allocation_mode: 'optimize_freely',
    allocation_config: {},
    notes: '',
    segments: segments.map((segment) => ({
      budget_id: segment.budget_id || null,
      source_budget_id: segment.source_budget_id || null,
      vertical: segment.vertical || null,
      vertical_ids: segment.vertical ? [Number(segment.vertical)] : [],
      location: segment.location || null,
      location_ids: segment.location ? [Number(segment.location)] : [],
      channel: segment.channel || null,
      amount: segment.amount,
      notes: segment.notes || '',
    })),
  }), [period, segments, selectedClient]);

  const runBudgetAction = useCallback(async (requestFn, successMessage) => {
    if (!selectedClient) {
      setBanner({ severity: 'error', message: 'Select a client first.' });
      return null;
    }
    setSaving(true);
    try {
      const response = await requestFn(buildPayload());
      const payload = response.data;
      setPageData(payload);
      setSegments(
        Array.isArray(payload?.segments) && payload.segments.length
          ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
          : [emptySegment(period)]
      );
      setBanner({ severity: 'success', message: successMessage });
      return payload;
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Budget action failed.') });
      return null;
    } finally {
      setSaving(false);
    }
  }, [buildPayload, period, selectedClient]);

  const saveDraft = useCallback(() => runBudgetAction(saveBudgetCenterDraft, 'Draft saved.'), [runBudgetAction]);
  const approveDraft = useCallback(() => runBudgetAction(approveBudgetCenter, 'Budget approved.'), [runBudgetAction]);

  const discardDraft = useCallback(async () => {
    if (!selectedClient) {
      return;
    }
    setSaving(true);
    try {
      const response = await discardBudgetCenterDraft({
        client_id: Number(selectedClient),
        period,
      });
      const payload = response.data;
      setPageData(payload);
      setSegments(
        Array.isArray(payload?.segments) && payload.segments.length
          ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
          : [emptySegment(period)]
      );
      setBanner({ severity: 'success', message: 'Draft discarded.' });
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to discard draft.') });
    } finally {
      setSaving(false);
    }
  }, [period, selectedClient]);

  const updateSegment = (index, field, value) => {
    setSegments((prev) => prev.map((segment, rowIndex) => (
      rowIndex === index ? { ...segment, [field]: value } : segment
    )));
  };

  const updatePendingAdjustment = (index, field, value) => {
    setSegments((prev) => prev.map((segment, rowIndex) => (
      rowIndex === index
        ? {
          ...segment,
          pendingAdjustment: {
            ...(segment.pendingAdjustment || {}),
            [field]: value,
          },
        }
        : segment
    )));
  };

  const ensureDraftSegment = useCallback(async (segment) => {
    if (pageData?.has_draft && segment.budget_id && pageData?.draft_budget_ids?.includes(segment.budget_id)) {
      return segment.budget_id;
    }
    const signature = segmentSignature(segment);
    const payload = await saveDraft();
    if (!payload) {
      return null;
    }
    const matchingSegment = (payload.segments || []).find(
      (row) => segmentSignature(normalizeSegment(row, payload.period || period)) === signature
    );
    return matchingSegment?.budget_id || null;
  }, [pageData, period, saveDraft]);

  const addAdjustment = useCallback(async () => {
    if (selectedSegmentIndex === null || !selectedSegment) {
      return;
    }
    const pendingAdjustment = selectedSegment.pendingAdjustment || {};
    if (!pendingAdjustment.amount_delta || !pendingAdjustment.effective_date) {
      setBanner({ severity: 'error', message: 'Adjustment amount and effective date are required.' });
      return;
    }

    const draftBudgetId = await ensureDraftSegment(selectedSegment);
    if (!draftBudgetId) {
      return;
    }

    setSaving(true);
    try {
      await api.request({
        url: `dashboard/client-budgets/${draftBudgetId}/adjustments/`,
        method: 'POST',
        payload: pendingAdjustment,
      });
      setBanner({ severity: 'success', message: 'Adjustment added.' });
      loadBudgetCenter();
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to add adjustment.') });
    } finally {
      setSaving(false);
    }
  }, [ensureDraftSegment, loadBudgetCenter, selectedSegment, selectedSegmentIndex]);

  const deleteAdjustment = useCallback(async (adjustmentId) => {
    if (!selectedSegment?.budget_id || !pageData?.draft_budget_ids?.includes(selectedSegment.budget_id)) {
      setBanner({ severity: 'info', message: 'Save a draft before removing adjustments.' });
      return;
    }
    setSaving(true);
    try {
      await api.request({
        url: `dashboard/client-budgets/${selectedSegment.budget_id}/adjustments/${adjustmentId}/`,
        method: 'DELETE',
      });
      setBanner({ severity: 'success', message: 'Adjustment removed.' });
      loadBudgetCenter();
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to remove adjustment.') });
    } finally {
      setSaving(false);
    }
  }, [loadBudgetCenter, pageData, selectedSegment]);

  const turnOffSegment = useCallback(async (segment) => {
    if (!segment) {
      return;
    }
    const effectiveAmount = Number(segment.effective_amount || segment.amount || 0);
    const monthToDateSpend = Number(segment.month_to_date_spend || 0);
    const amountDelta = monthToDateSpend - effectiveAmount;

    if (amountDelta >= 0) {
      setBanner({
        severity: 'info',
        message: `This segment is already at its lowest allowed effective budget of ${formatCurrency(monthToDateSpend)}.`,
      });
      return;
    }

    const draftBudgetId = await ensureDraftSegment(segment);
    if (!draftBudgetId) {
      return;
    }

    setSaving(true);
    try {
      await api.request({
        url: `dashboard/client-budgets/${draftBudgetId}/adjustments/`,
        method: 'POST',
        payload: {
          amount_delta: amountDelta.toFixed(2),
          effective_date: dayjs().format('YYYY-MM-DD'),
          notes: 'Turn off segment',
        },
      });
      setBanner({ severity: 'success', message: 'Segment reduced to the lowest allowed effective budget.' });
      loadBudgetCenter();
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to turn off segment.') });
    } finally {
      setSaving(false);
    }
  }, [ensureDraftSegment, loadBudgetCenter]);

  const openTurnOffDialog = useCallback((segment) => {
    setTurnOffDialogSegment(segment || null);
  }, []);

  const closeTurnOffDialog = useCallback(() => {
    setTurnOffDialogSegment(null);
  }, []);

  const confirmTurnOffSegment = useCallback(async () => {
    if (!turnOffDialogSegment) {
      return;
    }
    await turnOffSegment(turnOffDialogSegment);
    setTurnOffDialogSegment(null);
  }, [turnOffDialogSegment, turnOffSegment]);

  const openNotesDialog = useCallback((index) => {
    setSelectedSegmentIndex(index);
    setNotesDialogOpen(true);
  }, []);

  const closeNotesDialog = useCallback(() => {
    setNotesDialogOpen(false);
  }, []);

  return (
    <Page title="Budget Center" fullWidth>
      <Stack spacing={3}>
        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
            <Stack spacing={0.5}>
              <Typography variant="body1">
                Manage monthly budget segments and in-month adjustments.
              </Typography>
              <Typography color="text.secondary" variant="body2">
                All budget and spend figures shown here include client fees.
              </Typography>
            </Stack>
            <Button variant="text" onClick={() => setHelpOpen(true)}>
              How it works
            </Button>
          </Stack>
        </Box>

        {banner ? <Alert severity={banner.severity}>{banner.message}</Alert> : null}

        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              {canShowClientSelector ? (
                <Grid item xs={12} md={5}>
                  <Autocomplete
                    loading={clientLoading}
                    onChange={(_, option) => {
                      setSelectedClient(option?.value ? String(option.value) : '');
                      setSelectedClientOption(option || null);
                    }}
                    onInputChange={handleClientSearch}
                    options={clientOptions}
                    value={selectedClientOption}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                    renderOption={(props, option) => (
                      <li {...props} key={option?.value ?? option?.label}>
                        {option?.label || ''}
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Client" placeholder="Select client" />}
                  />
                </Grid>
              ) : (
                <Grid item xs={12} md={5}>
                  <TextField
                    disabled
                    fullWidth
                    label="Client"
                    value={pageData?.client?.name || memberships.find((membership) => membership.client_id)?.client_name || ''}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Month"
                  type="date"
                  value={period}
                  onChange={(event) => setPeriod(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Stack spacing={0.75}>
                  <Typography color="text.secondary" variant="caption">
                    Status
                  </Typography>
                  <Chip
                    color={getStatusChipColor(pageData?.status)}
                    label={formatStatusLabel(pageData?.status)}
                    sx={{ alignSelf: 'flex-start' }}
                    variant="filled"
                  />
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && selectedClient ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SummaryCard
                  label={pageData?.seeded_from_previous ? 'Current Starting Budget' : 'Current Budget'}
                  value={formatCurrency(currentSummary?.amount)}
                  helper={
                    pageData?.seeded_from_previous
                      ? `Copied from ${formatMonthLabel(pageData?.seed_source_period)}. Last month: ${formatCurrency(previousSummary?.effective_amount || previousSummary?.amount)}.`
                      : `Last month: ${formatCurrency(previousSummary?.effective_amount || previousSummary?.amount)}. ${currentSummary?.segment_count || segments.length} active segments in this month.`
                  }
                />
              </Grid>
              {showAdjustmentColumns ? (
                <Grid item xs={12} md={currentSummary?.adjustment_total && Number(currentSummary.adjustment_total) !== 0 ? 3 : 6}>
                  <SummaryCard
                    label="Adjustment Total"
                    value={formatCurrency(currentSummary?.adjustment_total)}
                    helper={
                      Number(currentSummary?.adjustment_total || 0) !== 0
                        ? 'Net in-month changes across the visible monthly set.'
                        : 'No in-month changes have been added yet.'
                    }
                  />
                </Grid>
              ) : null}
              {showAdjustmentColumns && Number(currentSummary?.adjustment_total || 0) !== 0 ? (
                <Grid item xs={12} md={3}>
                  <SummaryCard
                    label="Effective Budget"
                    value={formatCurrency(currentSummary?.effective_amount || currentSummary?.amount)}
                    helper="Current month starting budget plus approved in-month adjustments."
                  />
                </Grid>
              ) : null}
            </Grid>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="h6">Monthly Budget Table</Typography>
                    <Typography color="text.secondary" variant="body2">
                      {isLiveMonth
                        ? 'One row per budget segment. For live months, starting budget values are locked and changes should be made with adjustments.'
                        : 'Most clients can keep one flexible budget row. Only add segments if you want specific targets by vertical or market.'}
                    </Typography>
                  </Box>
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={() => setSegments((prev) => [...prev, emptySegment(period)])}
                  >
                      Add Segment
                    </Button>
                  </Stack>

                  {isLiveMonth ? (
                    <Alert severity="warning">
                      Live month: use adjustments to change existing segments. New segments can still be added.
                    </Alert>
                  ) : (
                    <Alert severity="info">
                      Fewer segments usually perform better because they give Lead Gurus more flexibility to optimize toward your cost and marketing goals.
                    </Alert>
                  )}

                  {pageData?.seeded_from_previous ? (
                    <Alert severity="success">
                      Started from {formatMonthLabel(pageData?.seed_source_period)} starting budget. Adjust segments as needed before saving.
                    </Alert>
                  ) : null}

                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Vertical</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Starting Budget</TableCell>
                          {showAdjustmentColumns ? <TableCell>Adjustments</TableCell> : null}
                          {showAdjustmentColumns ? <TableCell>Effective Budget</TableCell> : null}
                          {showAdjustmentColumns ? <TableCell>MTD Spend</TableCell> : null}
                          {showAdjustmentColumns ? <TableCell>Remaining</TableCell> : null}
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {segments.map((segment, index) => (
                          <TableRow key={`${segment.budget_id || 'new'}-${index}`} hover>
                            <TableCell sx={{ minWidth: 180 }}>
                              <TextField
                                disabled={isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids)}
                                fullWidth
                                select
                                size="small"
                                value={segment.vertical}
                                onChange={(event) => updateSegment(index, 'vertical', event.target.value)}
                              >
                                <MenuItem value="">All</MenuItem>
                                {verticalOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                              <TextField
                                disabled={isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids)}
                                fullWidth
                                select
                                size="small"
                                value={segment.location}
                                onChange={(event) => updateSegment(index, 'location', event.target.value)}
                              >
                                <MenuItem value="">All</MenuItem>
                                {locationOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                            <TableCell sx={{ minWidth: 140 }}>
                              <TextField
                                disabled={isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids)}
                                fullWidth
                                size="small"
                                inputProps={{ inputMode: 'decimal' }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                value={formatNumberInput(segment.amount)}
                                onChange={(event) => updateSegment(index, 'amount', event.target.value.replace(/,/g, ''))}
                              />
                            </TableCell>
                            {showAdjustmentColumns ? (
                              <TableCell>{formatCurrency(segment.adjustment_total)}</TableCell>
                            ) : null}
                            {showAdjustmentColumns ? (
                              <TableCell>{formatCurrency(segment.effective_amount || segment.amount)}</TableCell>
                            ) : null}
                            {showAdjustmentColumns ? (
                              <TableCell>{formatCurrency(segment.month_to_date_spend)}</TableCell>
                            ) : null}
                            {showAdjustmentColumns ? (
                              <TableCell>
                                {formatCurrency(
                                  Number(segment.effective_amount || segment.amount || 0)
                                    - Number(segment.month_to_date_spend || 0)
                                )}
                              </TableCell>
                            ) : null}
                            <TableCell>
                              <Chip
                                color={getStatusChipColor(segment.workflow_status)}
                                label={formatStatusLabel(segment.workflow_status)}
                                size="small"
                                variant="filled"
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <Button
                                size="small"
                                onClick={() => openNotesDialog(index)}
                              >
                                {segment.notes ? 'Edit Notes' : 'Notes'}
                              </Button>
                              {showAdjustmentColumns ? (
                                <Button
                                  size="small"
                                  startIcon={<EditOutlinedIcon />}
                                  onClick={() => {
                                    setSelectedSegmentIndex(index);
                                    setAdjustmentsDialogOpen(true);
                                  }}
                                >
                                  Add Adjustment
                                </Button>
                              ) : null}
                              {isLiveMonth ? (
                                <Button
                                  size="small"
                                  onClick={() => openTurnOffDialog(segment)}
                                >
                                  Turn Off
                                </Button>
                              ) : null}
                              <IconButton
                                onClick={() => setSegments((prev) => prev.filter((_, rowIndex) => rowIndex !== index))}
                                disabled={(isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids)) || segments.length === 1}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                  <Stack spacing={0.5}>
                    <Stack alignItems={{ xs: 'flex-start', sm: 'center' }} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Typography variant="h6">Monthly Actions</Typography>
                      <Chip
                        color={isLiveMonth ? 'warning' : 'success'}
                        label={isLiveMonth ? 'Live Month' : (isPlanningMode ? 'Planning Mode' : 'Month Setup')}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                    <Typography color="text.secondary" variant="body2">
                      {isLiveMonth
                        ? 'The month is live. Starting budget fields are locked and in-month changes should be made with adjustments.'
                        : 'Set starting budget amounts, then save a draft or approve the full monthly set when ready.'}
                    </Typography>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      disabled={saving}
                      startIcon={<SaveOutlinedIcon />}
                      variant="contained"
                      onClick={saveDraft}
                    >
                      Save Draft
                    </Button>
                    <Button
                      disabled={saving}
                      startIcon={<CheckCircleOutlineIcon />}
                      variant="outlined"
                      onClick={approveDraft}
                    >
                      Approve Budget
                    </Button>
                    <Button
                      disabled={saving || !pageData?.has_draft}
                      startIcon={<UndoOutlinedIcon />}
                      variant="text"
                      onClick={discardDraft}
                    >
                      Discard Changes
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography variant="h6">Change History</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Recent budget changes for this client and month.
                      </Typography>
                    </Box>
                    <Button
                      disabled={!(pageData?.history || []).length}
                      onClick={() => setHistoryOpen((prev) => !prev)}
                      variant="text"
                    >
                      {historyOpen ? 'Hide History' : 'Show History'}
                    </Button>
                  </Stack>

                  {historyOpen ? (
                    (pageData?.history || []).length ? (
                      <Stack spacing={1.5}>
                        {pageData.history.map((entry) => (
                          <Box
                            key={entry.id}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              p: 2,
                            }}
                          >
                            <Stack spacing={1}>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent="space-between"
                                spacing={1}
                              >
                                <Box>
                                  <Typography variant="subtitle2">
                                    {entry.summary}
                                  </Typography>
                                  <Typography color="text.secondary" variant="caption">
                                    {(entry.actor?.name || entry.actor?.email || 'System')} • {formatDateTime(entry.created_at)}
                                  </Typography>
                                </Box>
                                {entry.entity_label ? (
                                  <Chip label={entry.entity_label} size="small" variant="outlined" />
                                ) : null}
                              </Stack>

                              {entry.changed_fields?.length ? (
                                <Typography color="text.secondary" variant="body2">
                                  Changed: {entry.changed_fields.join(', ')}
                                </Typography>
                              ) : null}

                              {entry.changed_fields?.length && (entry.before || entry.after) ? (
                                <Stack spacing={0.75}>
                                  {entry.changed_fields.map((field) => {
                                    const beforeValue = entry.before?.[field];
                                    const afterValue = entry.after?.[field];
                                    return (
                                      <Typography key={`${entry.id}-${field}`} variant="body2">
                                        <Box component="span" sx={{ fontWeight: 600 }}>
                                          {field}
                                        </Box>
                                        : {formatHistoryValue(beforeValue)} {' -> '} {formatHistoryValue(afterValue)}
                                      </Typography>
                                    );
                                  })}
                                </Stack>
                              ) : null}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Alert severity="info">No budget change history yet for this month.</Alert>
                    )
                  ) : null}
                </Stack>
              </CardContent>
            </Card>

            <Dialog fullWidth maxWidth="md" onClose={() => setAdjustmentsDialogOpen(false)} open={adjustmentsDialogOpen}>
              <DialogTitle>Segment Adjustments</DialogTitle>
              <DialogContent>
                {selectedSegment ? (
                  <Stack spacing={2} sx={{ pt: 1 }}>
                    <Alert severity="info">
                      Starting budget stays separate from in-month changes. Use adjustments for increases or cuts that should affect only this segment.
                    </Alert>
                    <Typography variant="subtitle2">
                      {buildSegmentLabel(selectedSegment, optionSet)}
                    </Typography>
                    {(selectedSegment.adjustments || []).length ? (
                      (selectedSegment.adjustments || []).map((adjustment) => (
                        <Box key={adjustment.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                            <Box>
                              <Typography variant="body1">{formatCurrency(adjustment.amount_delta)}</Typography>
                              <Typography color="text.secondary" variant="body2">
                                Effective {dayjs(adjustment.effective_date).format('MMM D, YYYY')}
                              </Typography>
                              {adjustment.notes ? (
                                <Typography color="text.secondary" variant="body2">
                                  {adjustment.notes}
                                </Typography>
                              ) : null}
                            </Box>
                            <Button variant="text" onClick={() => deleteAdjustment(adjustment.id)}>
                              Remove
                            </Button>
                          </Stack>
                        </Box>
                      ))
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        No adjustments on this segment yet.
                      </Typography>
                    )}

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={selectedSegment.pendingAdjustment?.amount_delta || ''}
                          onChange={(event) => updatePendingAdjustment(selectedSegmentIndex, 'amount_delta', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Effective Date"
                          type="date"
                          value={selectedSegment.pendingAdjustment?.effective_date || period}
                          onChange={(event) => updatePendingAdjustment(selectedSegmentIndex, 'effective_date', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Notes"
                          value={selectedSegment.pendingAdjustment?.notes || ''}
                          onChange={(event) => updatePendingAdjustment(selectedSegmentIndex, 'notes', event.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAdjustmentsDialogOpen(false)}>Close</Button>
                <Button onClick={addAdjustment} variant="contained">
                  Add Adjustment
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog fullWidth maxWidth="sm" onClose={closeNotesDialog} open={notesDialogOpen}>
              <DialogTitle>Segment Notes</DialogTitle>
              <DialogContent>
                {selectedSegment ? (
                  <Stack spacing={2} sx={{ pt: 1 }}>
                    <Typography variant="subtitle2">
                      {buildSegmentLabel(selectedSegment, optionSet)}
                    </Typography>
                    <TextField
                      fullWidth
                      minRows={4}
                      multiline
                      value={selectedSegment.notes || ''}
                      onChange={(event) => updateSegment(selectedSegmentIndex, 'notes', event.target.value)}
                    />
                  </Stack>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={closeNotesDialog}>Close</Button>
              </DialogActions>
            </Dialog>

            <Dialog fullWidth maxWidth="sm" onClose={closeTurnOffDialog} open={Boolean(turnOffDialogSegment)}>
              <DialogTitle>Confirm Turn Off Segment</DialogTitle>
              <DialogContent>
                {turnOffDialogSegment ? (
                  <Stack spacing={2} sx={{ pt: 1 }}>
                    <Alert severity="warning">
                      Turning off a live segment creates a negative adjustment. It cannot reduce the effective budget below what has already been spent.
                    </Alert>
                    <Typography variant="subtitle2">
                      {buildSegmentLabel(turnOffDialogSegment, optionSet)}
                    </Typography>
                    <Box>
                      <Typography variant="body2">
                        Current effective budget: {formatCurrency(turnOffDialogSegment.effective_amount || turnOffDialogSegment.amount)}
                      </Typography>
                      <Typography variant="body2">
                        Month-to-date spend: {formatCurrency(turnOffDialogSegment.month_to_date_spend)}
                      </Typography>
                      <Typography variant="body2">
                        Lowest allowed effective budget after turn off: {formatCurrency(turnOffDialogSegment.month_to_date_spend)}
                      </Typography>
                    </Box>
                  </Stack>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={closeTurnOffDialog}>Cancel</Button>
                <Button color="warning" onClick={confirmTurnOffSegment} variant="contained">
                  Confirm Turn Off
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog fullWidth maxWidth="sm" onClose={() => setHelpOpen(false)} open={helpOpen}>
              <DialogTitle>How Budget Center Works</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Alert severity="info">
                    Set monthly budget segments for future months, then use adjustments to manage live-month changes.
                  </Alert>
                  <Box>
                    <Typography variant="subtitle2">1. Plan future months with starting budget amounts</Typography>
                    <Typography color="text.secondary" variant="body2">
                      Add, remove, or edit segment rows before the month is live. If a month has not been set up yet, it can start from the prior month&apos;s starting budget.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">2. Use adjustments once the month is live</Typography>
                    <Typography color="text.secondary" variant="body2">
                      For live months, existing starting budget rows stay locked. Use adjustments to increase, cut, or turn off a segment. New segments can still be added for launches like a new market.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">3. Effective budget cannot go below spend already delivered</Typography>
                    <Typography color="text.secondary" variant="body2">
                      The table shows month-to-date spend and remaining budget by segment. Current-month changes cannot reduce a segment below what has already been spent.
                    </Typography>
                  </Box>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setHelpOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          </>
        ) : null}
      </Stack>
    </Page>
  );
}
